import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useUsageTaxonomy } from './useUsageTaxonomy';
import { useAppMetadata } from './useAppMetadata';
import { slugify, normalizeForSearch } from '../../utils/slugify';

const chipStyle = (active) => ({
    padding: '4px 10px', borderRadius: '999px', fontSize: '0.8em', cursor: 'pointer',
    border: active ? '1px solid #2563eb' : '1px solid #d1d5db',
    background: active ? '#2563eb' : '#fff',
    color: active ? '#fff' : '#374151',
    whiteSpace: 'nowrap',
});

const primaryButtonStyle = {
    padding: '7px 14px', borderRadius: '6px', border: 'none',
    background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.88em',
    cursor: 'pointer', font: 'inherit',
};

const secondaryButtonStyle = {
    padding: '7px 14px', borderRadius: '6px', border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', fontWeight: 500, fontSize: '0.88em',
    cursor: 'pointer', font: 'inherit',
};

const InfoIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
        <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="8" cy="5" r="0.75" fill="currentColor" />
    </svg>
);

const LocalBadge = () => (
    <span style={{
        marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', fontSize: '0.72em',
        fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
        background: '#fef3c7', color: '#92400e',
    }}>
        Local
    </span>
);

// 6 caractères aléatoires, pour garantir l'unicité d'un id de sous-usage local
// même si deux bâtiments différents (ou le même, plus tard) créent un usage
// du même nom — un simple slug du libellé ne suffit plus dans ce cas.
const randomSuffix = () => Math.random().toString(36).slice(2, 8);

/* ─────────────────────────────────────────────
   Combobox de recherche pour choisir un usage (catégorie + sous-catégorie),
   remplaçant les deux <select> en cascade historiques. Un seul champ de
   recherche sur une liste aplatie de tous les sous-usages (plus besoin de
   choisir la catégorie d'abord), avec :
   - matching sur le libellé, les alias et les mots-clés, pas seulement le
     libellé affiché (ex. taper un synonyme stocké en alias retrouve la bonne
     entrée) ;
   - recherche insensible à la casse et aux accents ;
   - chips de catégorie en filtre secondaire, combinables avec le texte.
   Auto-suffisant : charge sa propre taxonomie et gère la création inline
   d'un nouveau sous-usage, toujours rattaché à une catégorie existante et
   toujours local au bâtiment courant (`buildingId`) — jamais ajouté au
   référentiel partagé depuis ce formulaire, pour ne pas le polluer avec des
   usages propres à un seul site.
───────────────────────────────────────────── */
const UsageCategoryPicker = ({ categoryId, subcategoryId, onChange, disabled = false, buildingId = null, buildingType = null }) => {
    const { categories, subcategoriesByCategory, loading, reload } = useUsageTaxonomy(buildingId);
    const { data: buildingTypeOptions } = useAppMetadata('building_types');
    const buildingTypeLabel = buildingTypeOptions.find((o) => o.value === buildingType)?.label || null;
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeCategoryFilter, setActiveCategoryFilter] = useState(null);
    const [showAllUsages, setShowAllUsages] = useState(false);
    const sectorFilterActive = !!buildingType && !showAllUsages;
    const [creating, setCreating] = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [newCategoryId, setNewCategoryId] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Liste aplatie : une ligne par sous-usage, avec sa catégorie parente et
    // tout le texte utilisable pour la recherche (libellé + alias + mots-clés
    // + libellé de la catégorie).
    const flatItems = useMemo(() => {
        const items = [];
        categories.forEach((cat) => {
            (subcategoriesByCategory[cat._id] || []).forEach((sub) => {
                const haystack = normalizeForSearch(
                    [sub.label, cat.label, ...(sub.aliases || []), ...(sub.keywords || [])].join(' ')
                );
                items.push({
                    subcategoryId: sub._id,
                    subcategoryLabel: sub.label,
                    categoryId: cat._id,
                    categoryLabel: cat.label,
                    categoryOrder: cat.order,
                    order: sub.order,
                    labelMatch: normalizeForSearch(sub.label),
                    haystack,
                    description: sub.description || '',
                    typicalEquipment: sub.typical_equipment || '',
                    isLocal: sub.scope === 'building',
                    buildingActivation: sub.building_activation || [],
                });
            });
        });
        return items;
    }, [categories, subcategoriesByCategory]);

    const selectedItem = flatItems.find((i) => i.subcategoryId === subcategoryId);
    const selectedCategoryOnly = !selectedItem && categoryId
        ? categories.find((c) => c._id === categoryId)
        : null;

    const normalizedQuery = normalizeForSearch(query);

    const filteredItems = useMemo(() => {
        let items = flatItems;
        if (sectorFilterActive) {
            items = items.filter((i) => i.buildingActivation.length === 0 || i.buildingActivation.includes(buildingType));
        }
        if (activeCategoryFilter) {
            items = items.filter((i) => i.categoryId === activeCategoryFilter);
        }
        if (normalizedQuery) {
            items = items.filter((i) => i.haystack.includes(normalizedQuery));
            // Pertinence : correspondance sur le libellé lui-même d'abord,
            // puis seulement via un alias/mot-clé.
            items = [...items].sort((a, b) => {
                const aLabelHit = a.labelMatch.includes(normalizedQuery) ? 0 : 1;
                const bLabelHit = b.labelMatch.includes(normalizedQuery) ? 0 : 1;
                if (aLabelHit !== bLabelHit) return aLabelHit - bLabelHit;
                if (a.categoryOrder !== b.categoryOrder) return a.categoryOrder - b.categoryOrder;
                return a.order - b.order;
            });
        } else {
            items = [...items].sort((a, b) =>
                a.categoryOrder !== b.categoryOrder ? a.categoryOrder - b.categoryOrder : a.order - b.order
            );
        }
        return items;
    }, [flatItems, sectorFilterActive, buildingType, activeCategoryFilter, normalizedQuery]);

    // Catégories à proposer dans les chips : uniquement celles ayant au moins
    // un sous-usage visible sous le filtre sectoriel actif (sans dépendre de la
    // recherche texte, pour ne pas faire disparaître/réapparaître les chips au
    // fil de la frappe).
    const visibleCategories = useMemo(() => {
        if (!sectorFilterActive) return categories;
        const categoriesWithMatch = new Set(
            flatItems
                .filter((i) => i.buildingActivation.length === 0 || i.buildingActivation.includes(buildingType))
                .map((i) => i.categoryId)
        );
        return categories.filter((c) => categoriesWithMatch.has(c._id));
    }, [categories, flatItems, sectorFilterActive, buildingType]);

    const groupedResults = useMemo(() => {
        const groups = [];
        const byCategoryId = {};
        filteredItems.forEach((item) => {
            if (!byCategoryId[item.categoryId]) {
                const group = { categoryId: item.categoryId, categoryLabel: item.categoryLabel, items: [] };
                byCategoryId[item.categoryId] = group;
                groups.push(group);
            }
            byCategoryId[item.categoryId].items.push(item);
        });
        return groups;
    }, [filteredItems]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setCreating(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const openPicker = () => {
        setIsOpen(true);
        setQuery('');
        setCreating(false);
        setShowAllUsages(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const selectItem = (item) => {
        onChange(item.categoryId, item.subcategoryId, item.subcategoryLabel);
        setIsOpen(false);
        setQuery('');
        setActiveCategoryFilter(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredItems.length > 0) selectItem(filteredItems[0]);
        }
    };

    const handleCreateSubcategory = async () => {
        const label = newLabel.trim();
        const targetCategoryId = activeCategoryFilter || newCategoryId;
        if (!label || !targetCategoryId) return;
        // Toujours local quand un bâtiment est connu — plus de choix "rendre
        // global" proposé ici, pour ne pas ajouter au référentiel partagé
        // depuis ce formulaire de saisie rapide.
        const isLocal = !!buildingId;
        const siblings = subcategoriesByCategory[targetCategoryId] || [];
        // Un sous-usage local a besoin d'un suffixe garanti unique : un même
        // libellé peut être créé indépendamment dans plusieurs bâtiments, un
        // simple slug entrerait en collision (l'_id est partagé par toute la
        // collection, global et local confondus).
        const _id = isLocal
            ? `${targetCategoryId}--${slugify(label)}-${randomSuffix()}`
            : `${targetCategoryId}--${slugify(label)}`;
        const order = siblings.length ? Math.max(...siblings.map((s) => s.order)) + 1 : 0;
        const payload = {
            _id, type: 'subcategory', parent_id: targetCategoryId, label, order, active: true,
            aliases: [], keywords: [],
            description: newDescription.trim(),
        };
        if (isLocal) {
            payload.scope = 'building';
            payload.building_id = buildingId;
        }
        await fetch(`${process.env.REACT_APP_API_URL}/load_new_usage_subcategory`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        await reload();
        onChange(targetCategoryId, _id, label);
        setCreating(false);
        setNewLabel('');
        setNewCategoryId('');
        setNewDescription('');
        setIsOpen(false);
        setQuery('');
        setActiveCategoryFilter(null);
    };

    if (loading) {
        return <div className="audit-usage-form-type">Chargement des catégories d'usage…</div>;
    }

    return (
        <div className="audit-usage-form-type ucp" ref={containerRef} style={{ position: 'relative' }}>
            {!isOpen ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                        type="button"
                        onClick={openPicker}
                        disabled={disabled}
                        style={{
                            textAlign: 'left', width: '100%', padding: '9px 12px',
                            border: '1px solid #d1d5db', borderRadius: '6px',
                            background: disabled ? '#f3f4f6' : '#fff',
                            cursor: disabled ? 'not-allowed' : 'pointer', font: 'inherit',
                        }}
                    >
                        {selectedItem ? (
                            <span>
                                <strong>{selectedItem.categoryLabel}</strong> › {selectedItem.subcategoryLabel}
                                {selectedItem.isLocal && <LocalBadge />}
                            </span>
                        ) : selectedCategoryOnly ? (
                            <span><strong>{selectedCategoryOnly.label}</strong> › <em style={{ color: '#9ca3af' }}>sous-usage à préciser</em></span>
                        ) : (
                            <span style={{ color: '#9ca3af' }}>— Sélectionner un usage —</span>
                        )}
                    </button>

                    {selectedItem && (selectedItem.description || selectedItem.typicalEquipment) && (
                        <span
                            style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, color: '#6b7280', cursor: 'help' }}
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                        >
                            <InfoIcon />
                            {showTooltip && (
                                <div style={{
                                    position: 'absolute', top: '22px', right: 0, zIndex: 20,
                                    width: '260px', background: '#fff', border: '1px solid #e5e7eb',
                                    borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                                    padding: '10px 12px', fontSize: '0.82em', color: '#374151',
                                    lineHeight: 1.4, fontWeight: 400, textAlign: 'left',
                                }}>
                                    {selectedItem.description && <div>{selectedItem.description}</div>}
                                    {selectedItem.typicalEquipment && (
                                        <div style={{ marginTop: selectedItem.description ? '6px' : 0, color: '#6b7280' }}>
                                            <strong>Équipements typiques :</strong> {selectedItem.typicalEquipment}
                                        </div>
                                    )}
                                </div>
                            )}
                        </span>
                    )}
                </div>
            ) : (
                <div style={{
                    border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.14)', overflow: 'hidden',
                }}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Rechercher un usage (ex. douche, plonge, sanitaire…)"
                        style={{
                            width: '100%', boxSizing: 'border-box', border: 'none',
                            borderBottom: '1px solid #e5e7eb', padding: '10px 12px', outline: 'none', font: 'inherit',
                        }}
                    />

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>
                        <button type="button" onClick={() => setActiveCategoryFilter(null)} style={chipStyle(activeCategoryFilter === null)}>
                            Toutes
                        </button>
                        {visibleCategories.map((cat) => (
                            <button key={cat._id} type="button"
                                onClick={() => setActiveCategoryFilter((prev) => (prev === cat._id ? null : cat._id))}
                                style={chipStyle(activeCategoryFilter === cat._id)}>
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {buildingType && (
                        <div style={{ padding: '4px 10px 8px', borderBottom: '1px solid #e5e7eb' }}>
                            <button type="button" onClick={() => setShowAllUsages((v) => !v)}
                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '0.78em', font: 'inherit' }}>
                                {sectorFilterActive
                                    ? `Filtré pour ${buildingTypeLabel || 'ce type de bâtiment'} — voir tous les usages`
                                    : `Revenir aux usages suggérés pour ${buildingTypeLabel || 'ce type de bâtiment'}`}
                            </button>
                        </div>
                    )}

                    <div style={creating ? undefined : { maxHeight: '260px', overflowY: 'auto' }}>
                        {creating ? (
                            <div style={{ padding: '12px' }}>
                                <div style={{
                                    fontSize: '0.75em', fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.05em', color: '#6b7280', marginBottom: '10px',
                                }}>
                                    Nouveau sous-usage{buildingId ? ' — local à ce bâtiment' : ''}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {!activeCategoryFilter && (
                                        <select autoFocus value={newCategoryId} onChange={(e) => setNewCategoryId(e.target.value)}>
                                            <option value="">— Catégorie —</option>
                                            {categories.map((c) => <option key={c._id} value={c._id}>{c.label}</option>)}
                                        </select>
                                    )}
                                    <input type="text" autoFocus={!!activeCategoryFilter} placeholder="Nom du sous-usage"
                                        value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
                                    <textarea placeholder="Description (optionnel)" rows={2}
                                        value={newDescription} onChange={(e) => setNewDescription(e.target.value)}
                                        style={{ font: 'inherit', resize: 'vertical' }} />
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                    <button type="button" onClick={handleCreateSubcategory} style={primaryButtonStyle}>Créer</button>
                                    <button type="button" onClick={() => setCreating(false)} style={secondaryButtonStyle}>Annuler</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {groupedResults.length === 0 && (
                                    <div style={{ padding: '14px 12px', color: '#6b7280', fontSize: '0.9em' }}>
                                        Aucun usage ne correspond.
                                        {sectorFilterActive && (
                                            <> <button type="button" onClick={() => setShowAllUsages(true)}
                                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>
                                                Voir tous les usages
                                            </button></>
                                        )}
                                    </div>
                                )}
                                {groupedResults.map((group) => (
                                    <div key={group.categoryId}>
                                        <div style={{
                                            padding: '6px 12px', fontSize: '0.75em', fontWeight: 700,
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                            color: '#6b7280', background: '#f9fafb', position: 'sticky', top: 0,
                                        }}>
                                            {group.categoryLabel}
                                        </div>
                                        {group.items.map((item) => (
                                            <button key={item.subcategoryId} type="button"
                                                onClick={() => selectItem(item)}
                                                style={{
                                                    display: 'block', width: '100%', textAlign: 'left', padding: '8px 16px',
                                                    border: 'none', font: 'inherit', cursor: 'pointer',
                                                    background: item.subcategoryId === subcategoryId ? '#eff6ff' : 'transparent',
                                                }}
                                                onMouseEnter={(e) => { if (item.subcategoryId !== subcategoryId) e.currentTarget.style.background = '#f9fafb'; }}
                                                onMouseLeave={(e) => { if (item.subcategoryId !== subcategoryId) e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                {item.subcategoryLabel}
                                                {item.isLocal && <LocalBadge />}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {!creating && (
                        <div style={{ borderTop: '1px solid #e5e7eb', padding: '8px 12px' }}>
                            <button type="button"
                                onClick={() => {
                                    setCreating(true);
                                    setNewLabel(query);
                                    setNewCategoryId(activeCategoryFilter || '');
                                    setNewDescription('');
                                }}
                                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '0.9em' }}>
                                + Créer un nouveau sous-usage
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UsageCategoryPicker;
